import { useContext } from 'react';
import { themeRegistry } from '../themes/registry';
import { ThemeContext } from '../themes/ThemeProvider';

export type ThemesGalleryProps = {
  /**
   * Override the registry read. When omitted, reads from the live
   * `themeRegistry.list()` — which is what the real gallery does.
   * Tests can pass a static array to avoid the registry side-effect.
   */
  themes?: ReadonlyArray<{
    slug: string;
    label: string;
    description?: string;
    tokens: Record<string, string>;
  }>;
  /**
   * Override the active-theme hook. Defaults to the live
   * `<ThemeProvider>` context. Tests pass a stub that returns a fixed
   * active slug and a recording `setTheme` function.
   */
  activeSlug?: string;
  onActivate?: (slug: string) => void;
};

/**
 * Preview every theme plugin registered at runtime. Each card shows
 * three color chips taken from the theme's `tokens` map, plus an
 * Activate button that calls the theme context's `setTheme(slug)`.
 * The active card is highlighted via `is-active`.
 *
 * The page intentionally reads `themeRegistry.list()` directly (not a
 * prop) so any future theme registered via `registerTheme()` shows up
 * here without touching this component.
 *
 * Rendering without `<ThemeProvider>` is supported: the context read
 * returns `null`, the active state becomes `undefined`, and the
 * Activate button is disabled. Tests can either mount with the
 * provider or pass `activeSlug` + `onActivate` overrides.
 */
function ThemesGallery({
  themes,
  activeSlug: activeSlugOverride,
  onActivate: onActivateOverride
}: ThemesGalleryProps = {}) {
  const ctx = useContext(ThemeContext);
  const list = themes ?? themeRegistry.list();
  const activeSlug = activeSlugOverride ?? ctx?.theme.slug;
  const onActivate = onActivateOverride ?? ctx?.setTheme;

  return (
    <div className="axi-gallery">
      <header className="axi-gallery__topbar">
        <div className="axi-gallery__brand">
          <span className="axi-gallery__brand-mark" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="12,3 21,12 12,21 3,12" />
            </svg>
          </span>
          <span>Axi Design · Themes</span>
        </div>
      </header>
      <main className="axi-gallery__main">
        <h1 className="axi-gallery__title">Theme Plugins</h1>
        <p className="axi-gallery__sub">
          当前 <code>themeRegistry</code> 中注册的 {list.length} 个主题。
          主题是纯 CSS 变量覆盖,以 slug 作为 <code>data-axi-theme</code> 属性值。
        </p>
        <ul className="axi-theme-list">
          {list.map((theme) => {
            const isActive = theme.slug === activeSlug;
            const chips = pickThreeChips(theme.tokens);
            return (
              <li
                key={theme.slug}
                className={`axi-theme-card${isActive ? ' is-active' : ''}`}
                data-theme-slug={theme.slug}
              >
                <div className="axi-theme-card__swatch" aria-hidden="true">
                  {chips.map((value, idx) => (
                    <span
                      key={`${theme.slug}-chip-${idx}`}
                      className="axi-theme-card__chip"
                      style={{ background: value }}
                    />
                  ))}
                </div>
                <h2 className="axi-theme-card__title">{theme.label}</h2>
                {theme.description ? (
                  <p className="axi-theme-card__desc">{theme.description}</p>
                ) : null}
                <button
                  type="button"
                  className="axi-theme-card__action"
                  onClick={() => onActivate?.(theme.slug)}
                  disabled={!onActivate || isActive}
                  aria-pressed={isActive}
                >
                  {isActive ? '✓ Active' : 'Activate'}
                </button>
              </li>
            );
          })}
        </ul>
      </main>
    </div>
  );
}

/**
 * Pull up to three visually meaningful color tokens from a theme's
 * override map. Heuristic: prefer `--axi-primary*`, then
 * `--axi-bg-*`, then anything that looks like a color literal. When
 * no colors exist (e.g. a pure typography theme) we return an empty
 * array; the swatch row simply renders nothing.
 */
function pickThreeChips(tokens: Record<string, string>): string[] {
  const priority = [
    '--axi-primary',
    '--axi-primary-hover',
    '--axi-bg-page',
    '--axi-bg-surface',
    '--axi-bg-elevated',
    '--axi-text',
    '--axi-border'
  ];
  const out: string[] = [];
  for (const key of priority) {
    const value = tokens[key];
    if (value && /^#[0-9a-fA-F]{3,8}$/.test(value)) {
      out.push(value);
    }
    if (out.length === 3) break;
  }
  return out;
}

export default ThemesGallery;